import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const { FiBell, FiMapPin, FiDollarSign, FiZap, FiFilter, FiX, FiCheckCircle, FiChevronDown, FiEdit2, FiTrash2 } = FiIcons;

const JobAlerts = () => {
  const { session } = useAuth();
  const { data, refreshData } = useProfile();
  const jobAlerts = data.jobAlerts;

  const [showFilters, setShowFilters] = useState(false);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({ title: '', company: '', location: '', salary_display: '', match: 85, type: 'Full-time' });
  const [editingAlertId, setEditingAlertId] = useState(null);

  const openEditAlert = (alert) => {
    setNewAlert({
      title: alert.title,
      company: alert.company === 'Any Company' ? '' : alert.company,
      location: alert.location === 'Remote' ? '' : alert.location,
      salary_display: alert.salary_display === 'Competitive' ? '' : alert.salary_display,
      match: alert.match,
      type: alert.type || 'Full-time'
    });
    setEditingAlertId(alert.id);
    setShowAddAlert(true);
  };

  const openCreateAlert = () => {
    setNewAlert({ title: '', company: '', location: '', salary_display: '', match: 85, type: 'Full-time' });
    setEditingAlertId(null);
    setShowAddAlert(true);
  };

  const deleteAlert = async (id) => {
    if (!window.confirm("Are you sure you want to delete this alert?")) return;
    try {
      const { error } = await supabase.from('job_alerts').delete().eq('id', id);
      if (error) throw error;
      refreshData();
    } catch (err) {
      console.error("Failed to delete alert:", err);
      alert("Failed to delete alert. Please try again.");
    }
  };
  const [filters, setFilters] = useState({
    minSalary: 0,
    remoteOnly: false,
    minMatch: 80,
    jobType: 'All'
  });

  const filteredJobs = useMemo(() => {
    return jobAlerts.filter(job => {
      const matchSalary = (job.salary || 0) >= filters.minSalary;
      const matchRemote = !filters.remoteOnly || job.location.toLowerCase().includes('remote');
      const matchScore = job.match >= filters.minMatch;
      const matchType = filters.jobType === 'All' || job.type === filters.jobType;
      return matchSalary && matchRemote && matchScore && matchType;
    });
  }, [filters, jobAlerts]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex justify-between items-center bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Personalized Job Alerts</h1>
          <p className="text-gray-500 mt-1">Real-time matches based on your optimized profile.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
              showFilters ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={FiFilter} /> {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
          <button onClick={openCreateAlert} className="bg-blue-900 text-white px-6 py-2 rounded-xl flex items-center gap-2 font-bold hover:bg-black transition-all">
            <SafeIcon icon={FiBell} /> New Alert
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <AnimatePresence>
          {showFilters && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm sticky top-24">
                <h3 className="font-black text-xs text-gray-400 uppercase tracking-widest mb-6">Custom Filters</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-3">Min. Base Salary ($k)</label>
                    <input 
                      type="range" min="0" max="300000" step="10000"
                      value={filters.minSalary}
                      onChange={(e) => setFilters({...filters, minSalary: parseInt(e.target.value)})}
                      className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between mt-2 text-[10px] font-black text-blue-600">
                      <span>$0</span>
                      <span>${filters.minSalary / 1000}k+</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-3">Min. Match Score</label>
                    <div className="flex gap-2">
                      {[80, 85, 90, 95].map(score => (
                        <button 
                          key={score}
                          onClick={() => setFilters({...filters, minMatch: score})}
                          className={`flex-1 py-2 rounded-lg text-[10px] font-black border transition-all ${
                            filters.minMatch === score ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-100 text-gray-400'
                          }`}
                        >
                          {score}%+
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-50">
                    <label className="flex items-center justify-between cursor-pointer group">
                      <span className="text-sm font-bold text-gray-700 group-hover:text-blue-600">Remote Only</span>
                      <div className="relative inline-block w-10 h-6">
                        <input 
                          type="checkbox" className="hidden"
                          checked={filters.remoteOnly}
                          onChange={() => setFilters({...filters, remoteOnly: !filters.remoteOnly})}
                        />
                        <div className={`w-10 h-6 rounded-full transition-colors ${filters.remoteOnly ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                        <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${filters.remoteOnly ? 'translate-x-4' : ''}`}></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={showFilters ? 'lg:col-span-3 space-y-4' : 'lg:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-4'}>
          {filteredJobs.length > 0 ? filteredJobs.map((job, idx) => (
            <motion.div 
              key={job.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-blue-200 transition-all group flex gap-6"
            >
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-2xl font-black text-gray-300 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors shrink-0">
                {job.company.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <h3 className="text-lg font-black text-gray-900 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{job.title}</h3>
                    <p className="text-gray-500 font-bold">{job.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                      job.match >= 90 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {job.match}% Match
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); openEditAlert(job); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="Edit Alert">
                      <SafeIcon icon={FiEdit2} size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteAlert(job.id); }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="Remove Alert">
                      <SafeIcon icon={FiTrash2} size={14} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-400 mt-4">
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiMapPin} className="text-blue-500" /> {job.location}</span>
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiDollarSign} className="text-green-500" /> {job.salaryDisplay}</span>
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiCheckCircle} className="text-purple-500" /> {job.type}</span>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="flex gap-2">
                    {job.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-2 py-1 bg-gray-50 text-gray-500 rounded text-[10px] uppercase font-black">#{tag}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      alert(`Redirecting to ${job.company}'s external application portal...`);
                    }}
                    className="bg-gray-900 text-white px-6 py-2 rounded-xl text-xs font-black hover:bg-blue-600 transition-all flex items-center gap-2"
                  >
                    Apply <SafeIcon icon={FiZap} />
                  </button>
                </div>
              </div>
            </motion.div>
          )) : (
            <div className="col-span-full py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
              <SafeIcon icon={FiX} className="text-4xl text-gray-200 mx-auto mb-4" />
              <p className="text-gray-500 font-bold">No jobs match your current filters.</p>
              <button 
                onClick={() => setFilters({ minSalary: 0, remoteOnly: false, minMatch: 80, jobType: 'All' })}
                className="mt-4 text-blue-600 font-black text-sm uppercase hover:underline"
              >
                Reset All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showAddAlert && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddAlert(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[450px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">{editingAlertId ? "Edit Job Alert" : "Create Job Alert"}</h3>
                <button onClick={() => setShowAddAlert(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Job Title (e.g. Product Manager)"
                  value={newAlert.title}
                  onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Company (Optional)"
                  value={newAlert.company}
                  onChange={(e) => setNewAlert({...newAlert, company: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Location (e.g. Remote, NY)"
                  value={newAlert.location}
                  onChange={(e) => setNewAlert({...newAlert, location: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Salary Display (e.g. $120k - $150k)"
                  value={newAlert.salary_display}
                  onChange={(e) => setNewAlert({...newAlert, salary_display: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <select
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                  value={newAlert.type}
                  onChange={(e) => setNewAlert({...newAlert, type: e.target.value})}
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Part-time">Part-time</option>
                </select>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">Target Match Score</label>
                  <input
                    type="range" min="50" max="100"
                    value={newAlert.match}
                    onChange={(e) => setNewAlert({...newAlert, match: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="text-right text-xs font-bold text-blue-600 mt-1">{newAlert.match}%</div>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowAddAlert(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newAlert.title) return;
                    let error;
                    const payload = {
                      profile_id: session.user.id,
                      title: newAlert.title,
                      company: newAlert.company || 'Any Company',
                      location: newAlert.location || 'Remote',
                      salary_display: newAlert.salary_display || 'Competitive',
                      match: newAlert.match,
                      type: newAlert.type,
                      tags: ['new', newAlert.type.toLowerCase()]
                    };

                    if (editingAlertId) {
                      const res = await supabase.from('job_alerts').update(payload).eq('id', editingAlertId);
                      error = res.error;
                    } else {
                      const res = await supabase.from('job_alerts').insert([payload]);
                      error = res.error;
                    }

                    if (!error) {
                      setNewAlert({ title: '', company: '', location: '', salary_display: '', match: 85, type: 'Full-time' });
                      setEditingAlertId(null);
                      setShowAddAlert(false);
                      refreshData();
                    } else {
                      alert('Failed to save alert');
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  {editingAlertId ? "Update Alert" : "Save Alert"}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JobAlerts;